// src/admin/AdminLayout.js
// Indigo/Purple theme — distinct from merchant rose
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Package, ShoppingBag,
  LogOut, Menu, X, ChevronRight, Store, RefreshCw, Tag, Brain,
} from 'lucide-react';
import { useApp } from '../context/AppContext';

const NAV = [
  { to: '/admin',           icon: LayoutDashboard, label: 'Dashboard', exact: true  },
  { to: '/admin/merchants', icon: Users,            label: 'Merchants', exact: false },
  { to: '/admin/orders',    icon: ShoppingBag,      label: 'Orders',    exact: false },
  { to: '/admin/products',  icon: Package,          label: 'Products',  exact: false },
  { to: '/admin/coupons',   icon: Tag,              label: 'Coupons',   exact: false },
  { to: '/admin/returns',   icon: RefreshCw,        label: 'Returns',   exact: false },
  { to: '/admin/ceo-agent', icon: Brain,            label: 'CEO Agent', exact: false },
];

// Bottom nav shows only 5 most important on mobile
const MOBILE_NAV = [
  { to: '/admin',           icon: LayoutDashboard, label: 'Home',      exact: true  },
  { to: '/admin/merchants', icon: Users,            label: 'Merchants', exact: false },
  { to: '/admin/orders',    icon: ShoppingBag,      label: 'Orders',    exact: false },
  { to: '/admin/coupons',   icon: Tag,              label: 'Coupons',   exact: false },
  { to: '/admin/ceo-agent', icon: Brain,            label: 'Aria',      exact: false },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useApp();
  const location          = useLocation();
  const navigate          = useNavigate();
  const [open, setOpen]   = useState(false);

  const isActive = ({ to, exact }) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  const currentNav = NAV.find((n) => isActive(n));

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-indigo-800/50 flex-shrink-0">
        <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-violet-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-bold">⚙</span>
          </div>
          <div>
            <p className="font-display font-bold text-white text-base leading-none">CoupleCart</p>
            <p className="text-indigo-400 text-[10px] font-body mt-0.5 tracking-widest uppercase font-bold">Admin Panel</p>
          </div>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map(({ to, icon: Icon, label, exact }) => {
          const active = isActive({ to, exact });
          return (
            <Link key={to} to={to} onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-body font-medium transition-all ${
                active
                  ? 'bg-indigo-600 text-white shadow-[0_4px_15px_rgba(99,102,241,0.4)]'
                  : 'text-indigo-200/60 hover:bg-indigo-800/40 hover:text-white'
              }`}>
              <Icon size={18} /> {label}
              {active && <ChevronRight size={14} className="ml-auto" />}
            </Link>
          );
        })}
      </nav>

      {/* User + logout */}
      <div className="px-3 py-4 border-t border-indigo-800/50 flex-shrink-0 space-y-1">
        <div className="flex items-center gap-3 px-4 py-3 bg-indigo-800/40 rounded-2xl">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-violet-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {user?.displayName?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-body text-sm font-medium truncate">{user?.displayName}</p>
            <p className="text-indigo-400 font-body text-xs font-bold uppercase tracking-wider">Admin</p>
          </div>
        </div>
        <button onClick={() => { logout(); navigate('/'); }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-indigo-200/60 hover:bg-red-500/10 hover:text-red-400 text-sm font-body transition-all">
          <LogOut size={16} /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-indigo-950 fixed top-0 left-0 bottom-0 z-40">
        <SidebarInner />
      </aside>

      {/* Mobile backdrop */}
      {open && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Mobile sidebar drawer */}
      <aside className={`fixed top-0 left-0 bottom-0 w-64 bg-indigo-950 z-50 flex flex-col md:hidden transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex justify-end p-4 flex-shrink-0">
          <button onClick={() => setOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-indigo-300">
            <X size={20} />
          </button>
        </div>
        <SidebarInner />
      </aside>

      {/* Main content */}
      <div className="flex-1 md:ml-60 flex flex-col min-h-screen">

        {/* Topbar */}
        <header className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              {/* Hamburger — mobile only */}
              <button onClick={() => setOpen(true)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
                <Menu size={22} className="text-gray-600" />
              </button>
              <div>
                <h1 className="font-display font-bold text-gray-900 text-base leading-none">
                  {currentNav?.label || 'Admin'}
                </h1>
                <p className="text-indigo-500 font-body text-xs font-bold uppercase tracking-wider mt-0.5">Admin Panel</p>
              </div>
            </div>
            <Link to="/" target="_blank"
              className="flex items-center gap-1.5 text-xs font-body text-gray-500 hover:text-indigo-500 border border-gray-200 hover:border-indigo-300 px-3 py-2 rounded-xl transition-all">
              <Store size={14} /> View Store
            </Link>
          </div>
        </header>

        {/* Page content — extra bottom padding on mobile for bottom nav */}
        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8">
          {children}
        </main>
      </div>

      {/* ── Mobile bottom navigation bar ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-indigo-950 border-t border-indigo-800/50 flex items-center">
        {MOBILE_NAV.map(({ to, icon: Icon, label, exact }) => {
          const active = isActive({ to, exact });
          return (
            <Link key={to} to={to}
              className={`flex-1 flex flex-col items-center justify-center py-2.5 gap-0.5 transition-all ${
                active ? 'text-indigo-400' : 'text-indigo-200/40'
              }`}>
              <Icon size={20} />
              <span className="text-[10px] font-body font-medium">{label}</span>
              {active && <span className="w-1 h-1 bg-indigo-400 rounded-full" />}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
